import React from 'react';
import styled from 'styled-components';
import { EuiIcon, EuiBadge } from '@elastic/eui';
import utilsService from '../../../../../servises/utils.service';

const CardWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 4px 0;
`;

const NicHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  padding: 10px 12px;
  
  .nic-details {
    display: flex;
    align-items: center;
    gap: 8px;
    
    .nic-icon {
      color: #48bb78;
      background: rgba(72, 187, 120, 0.15);
      padding: 6px;
      border-radius: 6px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    
    .nic-text {
      display: flex;
      flex-direction: column;
      gap: 2px;
      
      .label {
        font-size: 9px;
        color: rgba(255, 255, 255, 0.4);
        font-weight: 600;
        letter-spacing: 0.5px;
        text-transform: uppercase;
      }
      
      .name {
        font-size: 13px;
        font-weight: 700;
        color: #fff;
        font-family: Consolas, monospace;
      }
    }
  }
  
  .connections-badge {
    font-family: Consolas, monospace;
    font-weight: 700;
  }
`;

const TrafficGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 8px;
`;

const TrafficCard = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.04);
  border-radius: 8px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  
  .header-row {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 10px;
    color: rgba(255, 255, 255, 0.4);
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    
    .icon {
      color: ${props => props.color || 'rgb(0, 128, 213)'};
    }
  }
  
  .speed-value {
    font-size: 16px;
    font-weight: 800;
    color: #fff;
    font-family: Consolas, monospace;
    margin-top: 2px;
  }
`;

const CumulativeStats = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  border-top: 1px solid rgba(255, 255, 255, 0.04);
  padding-top: 8px;
  margin-top: 4px;
`;

const StatRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  padding: 2px 4px;
  
  .label-group {
    display: flex;
    align-items: center;
    gap: 6px;
    color: rgba(255, 255, 255, 0.45);
    font-weight: 500;
    
    .icon {
      opacity: 0.6;
    }
  }
  
  .value {
    color: rgba(255, 255, 255, 0.9);
    font-weight: 600;
    font-family: Consolas, monospace;
  }
`;

const ServerNetworkWidget = ({ server }) => {
    const rx = utilsService.getFromMetrics(server, 'rx', 0);
    const tx = utilsService.getFromMetrics(server, 'tx', 0);
    const rxGap = utilsService.getFromMetrics(server, 'rx_gap', 0);
    const txGap = utilsService.getFromMetrics(server, 'tx_gap', 0);
    const nic = utilsService.getFromMetrics(server, 'nic', 'N/A');
    const connections = utilsService.getFromMetrics(server, 'connections', 0);

    const rx_ = utilsService.formatBytes(rx);
    const tx_ = utilsService.formatBytes(tx);
    const rxGap_ = utilsService.formatBytes(rxGap);
    const txGap_ = utilsService.formatBytes(txGap);

    return (
        <CardWrapper>
            {/* Active Interface & Connection Count */}
            <NicHeader>
                <div className="nic-details">
                    <div className="nic-icon">
                        <EuiIcon type="subnetwork" size="s" />
                    </div>
                    <div className="nic-text">
                        <span className="label">Active Interface</span>
                        <span className="name">{ nic }</span>
                    </div>
                </div>
                <EuiBadge color="hollow" className="connections-badge">
                    { connections } Active
                </EuiBadge>
            </NicHeader>

            {/* Current Speeds */}
            <TrafficGrid>
                <TrafficCard color="#48bb78">
                    <div className="header-row">
                        <EuiIcon type="download" size="s" className="icon" />
                        <span>Download</span>
                    </div>
                    <div className="speed-value">{ rxGap_.value } { rxGap_.unit }/s</div>
                </TrafficCard>

                <TrafficCard color="rgb(0, 128, 213)">
                    <div className="header-row">
                        <EuiIcon type="upload" size="s" className="icon" />
                        <span>Upload</span>
                    </div>
                    <div className="speed-value">{ txGap_.value } { txGap_.unit }/s</div>
                </TrafficCard>
            </TrafficGrid>

            {/* Cumulative Telemetry Stats */}
            <CumulativeStats>
                <StatRow>
                    <div className="label-group">
                        <EuiIcon type="inputOutput" size="s" className="icon" />
                        <span>Total Received</span>
                    </div>
                    <span className="value">{ rx_.value } { rx_.unit }</span>
                </StatRow>
                <StatRow>
                    <div className="label-group">
                        <EuiIcon type="inputOutput" size="s" className="icon" />
                        <span>Total Transferred</span>
                    </div>
                    <span className="value">{ tx_.value } { tx_.unit }</span>
                </StatRow>
            </CumulativeStats>
        </CardWrapper>
    );
};

export default ServerNetworkWidget;